import {
  all,
  call,
  put,
  select,
  takeLeading,
} from 'redux-saga/effects';

import { get, putRequest } from '@dmi/shared/utils/request';

import {
  changeStep,
  changeValidationFailure,
  flexDraftChangeFailure,
  flexDraftChangeSuccess,
  flexDraftFetchStatusFailure,
  flexDraftFetchStatusSuccess,
} from './actions';
import {
  DEFAULT_FLEX_DRAFT_CHANGE_ERRORS,
  FLEX_DRAFT_CHANGE,
  FLEX_DRAFT_FETCH_STATUS,
  REASON_CODE_DICTIONARY,
} from './constants';
import { parseReasonCodes } from './helpers';
import { selectConfirmationData, selectForm } from './selectors';
import makeSelectMain from '../../Main/selectors';
import makeSelectMobile from '../../Mobile/selectors';

export function* flexDraftChangeSaga(
  { baseUrl, navigationHandler, navigationReplaceHandler },
  { payload },
) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const { body, loanNumber } = payload;
    const endpoint = `${baseUrl}/payments/flexdraft/${loanNumber}`;
    const { confirmationNumber, error } = yield call(putRequest, endpoint, body);
    if (error) {
      const { reasons } = error;
      if (reasons.length) {
        const { flexDraft } = parseReasonCodes(reasons, DEFAULT_FLEX_DRAFT_CHANGE_ERRORS);
        yield put(changeValidationFailure(flexDraft));
        const { screen } = REASON_CODE_DICTIONARY[reasons[0]];
        if (isMobile) {
          navigationHandler('FlexDraftSetupOverview');
          if (screen) {
            navigationHandler(screen);
          }
        } else {
          yield put(changeStep(1));
        }
      } else {
        throw new Error('serviceError');
      }
    } else {
      yield put(flexDraftChangeSuccess({ confirmationNumber }));
      if (isMobile) {
        /*
          Flex Draft form values reset upon submission in mobile native.
          This passes confirmationData and draftSchedule as navigation params to
          the Confirmation screen to prevent displaying stale data.
        */
        const confirmationData = yield select(selectConfirmationData());
        const { draftSchedule } = yield select(selectForm('scheduleSelect'));
        const properNavigationHandler = isNativeMobile ?
          navigationReplaceHandler :
          navigationHandler;
        properNavigationHandler('FlexDraftConfirmation', { confirmationData, draftSchedule });
      } else {
        yield put(changeStep(4));
      }
    }
  } catch (error) {
    yield put(flexDraftChangeFailure());
    if (isMobile) {
      const confirmationData = yield select(selectConfirmationData());
      const properNavigationHandler = isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('FlexDraftConfirmation', { confirmationData });
    } else {
      yield put(changeStep(4));
    }
  }
}

export function* flexDraftFetchStatusSaga({ baseUrl }) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/payments/flexdraft/${loanNumber}`;
    const { statusData } = yield call(get, endpoint);
    const {
      allowedOperation,
      allowedSetting,
      pendingSetting,
      statement,
      status,
    } = statusData;
    yield put(flexDraftFetchStatusSuccess({
      allowedOperation,
      allowedSetting,
      pendingSetting,
      statement,
      status,
    }));
  } catch {
    yield put(flexDraftFetchStatusFailure({ error: true }));
  }
}

export default (options) => function* watcherSaga() {
  yield all([
    takeLeading(FLEX_DRAFT_CHANGE, flexDraftChangeSaga, options),
    takeLeading(FLEX_DRAFT_FETCH_STATUS, flexDraftFetchStatusSaga, options),
  ]);
};
